import { Topic, Warehouse } from "@mui/icons-material";
import { Chip, CircularProgress, LinearProgress, Tooltip, Typography } from "@mui/material";
import { cloneDeep, debounce } from 'lodash';
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../Api";
import { IMaterial, IStorageRequest } from "../../interfaces";
import { IDocument } from "../../interfaces/Common/IDocument";
import { BreadcrumbBar } from "../Breadcrumb";
import { ExpandableContainer } from "../Shared/ExpandableContainer";
import FormNavigation from "../Shared/FormNavigation";
import { DeliveryInformationForm } from "./DeliveryInformation";
import { HazardousDeclarationForm } from "./HazardousDeclaration";
import "./index.scss";
import { MaterialInformationForm } from "./MaterialInformation";
import { StorageRequestActions } from "./StorageRequestActions";
import { StorageRequestDetailForm } from "./StorageRequestDetail";


export const StorageRequest: React.FC = ({
    ...props
}) => {

    const [storageRequestId, setStorageRequestId] = useState<string>();
    const [linkedC, setLinkedC] = useState<{ contractId: string, reference: string } | null>(null);
    const [createdBy, setCreatedBy] = useState<string>();
    const [dateCreated, setDateCreated] = useState<string>();
    const [reference, setReference] = useState<string>();
    const [discrepancyAlertSent, setDiscrepancyAlertSent] = useState<boolean>(false);
    const [status, setStatus] = useState<number>();
    const [materials, setMaterials] = useState<IMaterial[]>([]);
    const [staticMaterials, setStaticMaterials] = useState(false);
    const [documents, setDocuments] = useState<IDocument[]>([]);

    const [model, setModel] = useState<IStorageRequest>();
    const [bread, setBread] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const { srId } = useParams();

    const navigate = useNavigate();

    useEffect(() => {

        let requestedId: any = srId;
        if (requestedId) setBread("Update");
        else {
            setBread("Create");
            requestedId = null;
        }

        //when opening an existing request we will pass
        //in the id on the fetch request.

        fetchFormData(requestedId).then((data: any) => {
            populateForm(data);
        });

    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

    }, [materials, model])

    const fetchFormData = async (id: string | null | undefined) => {

        if (id) {

            return Api.StorageRequests.get(id);

        } else return Api.StorageRequests.create().then((data) => {


            //update the url so page reloads dont cause
            //multiple requests to be created

            var sr = data as IStorageRequest;
            window.history.pushState("Update", "Update Storage Request", "/storagerequest/update/" + sr.storageRequestId);

            return data;
        });

    }

    const populateForm = (data: any) => {

        if (data.storageRequestId)
            setStorageRequestId(data.storageRequestId);

        if (data.createdBy)
            setCreatedBy(data.createdBy);

        if (data.dateCreated)
            setDateCreated(data.dateCreated);

        if (data.reference)
            setReference(data.reference);

        if (data.status)
            setStatus(data.status);

        if (data.materialsStatic) {
            setMaterials(data.materialsStatic);
            setStaticMaterials(true);
        }
        else if (data.materials)
            setMaterials(data.materials);

        if (data.documents)
            setDocuments(data.documents);

        setModel(data);

        setDiscrepancyAlertSent(data.discrepancyAlertSent ?? data.status > 3)

        if (data.contract)
            setLinkedC({
                contractId: data.contract.contractId,
                reference: data.contract.contractReference
            })

        setLoading(false);
    }

    const handleChange = (property: string, value: string) => {
        setUnsavedChanges(true);

        let storageRequest = getRequestModel() as any;

        storageRequest[property] = value;


        setModel(storageRequest as IStorageRequest);

        handleSaveDebounced(storageRequest);

    }

    const handleSave = (model: IStorageRequest) => {
        Api.StorageRequests.save(model);
        setUnsavedChanges(false);
    }
    const handleSaveDebounced = useRef(debounce(handleSave, 1000)).current;


    const onUpdateMaterials = (newMaterials: IMaterial[] | null | undefined) => {

        if (newMaterials) {



            let removed = materials.filter(x => !newMaterials.map(x => x.materialId).includes(x.materialId));
            removed.forEach(async (material) => {
                await onRemoveMaterial(material);
            });

            let created = newMaterials.filter(x => x.materialId === undefined);
            created.forEach(async (material) => {
                material.storageRequestId = storageRequestId;
                await onUpdateMaterial(null, material);
            });
        }

    }

    const onRemoveMaterial = async (material: IMaterial) => {
        if (storageRequestId && material.materialId)
            await Api.StorageRequests.Materials.remove(storageRequestId, material.materialId).then((response: any) => {
                let updated = cloneDeep(materials).filter(x => x.materialId !== material.materialId);
                setMaterials(updated);
            })
    }

    const onUpdateMaterial = async (material: IMaterial | null | undefined, draft?: IMaterial) => {

        let newMaterials = cloneDeep(materials);

        if (material) {

            await Api.Materials.update(material).then((response) => {

                let materialResponse = response as IMaterial;

                newMaterials = newMaterials.filter(x =>
                    x.materialId !== material.materialId);

                newMaterials.push(materialResponse);

            }).catch((e) => {
                console.log("error", e);
            });
        }

        if (draft) {
            await Api.Materials.create(draft).then((response) => {

                let materialResponse = response as IMaterial;

                newMaterials.push(materialResponse);

            });
        }

        fetchFormData(storageRequestId).then((data: any) => {
            populateForm(data);
        });

    }

    const onImportComplete = (imported: IMaterial[]) => {
        let newMaterials = cloneDeep(materials);
        newMaterials = newMaterials.concat(imported);
        setMaterials(newMaterials);
    }

    const onSubmit = async () => {
        setLoading(true);
        return Api.StorageRequests.submit(storageRequestId as string).then(async (r) => {

            fetchFormData(storageRequestId).then((data: any) => {
                populateForm(data);
            });

        });
    }

    const onDelete = async () => {
        return Api.StorageRequests.delete(storageRequestId as string).then(async (r) => {
            navigate('/storagerequest');
        });
    }

    const onApprove = async () => {
        return Api.StorageRequests.approve(storageRequestId as string).then(async (r) => {

            fetchFormData(storageRequestId).then((data: any) => {
                populateForm(data);
            });

        });
    }

    const onAlert = async () => {
        await Api.StorageRequests.alert(storageRequestId as string).then(() => {
            setDiscrepancyAlertSent(true);
        })
    }

    const onComplete = async (cancellationReason?: string) => {
        return Api.StorageRequests.complete(storageRequestId as string, cancellationReason).then(async (r) => {

            fetchFormData(storageRequestId).then((data: any) => {
                populateForm(data);
            });

        });
    }

    const onReject = async (reason: string) => {
        return Api.StorageRequests.reject(storageRequestId as string, reason).then(async (r) => {

            fetchFormData(storageRequestId).then((data: any) => {
                populateForm(data);
            });

        });
    }

    const onClose = async () => {
        return Api.StorageRequests.close(storageRequestId as string).then(async (r) => {

            fetchFormData(storageRequestId).then((data: any) => {
                populateForm(data);
            });

        });
    }

    const getRequestModel = () => {

        if (model) {
            let storageRequest: IStorageRequest = {
                ...model,
                storageRequestId,
                status,
                reference,
                createdBy,
                dateCreated,
                materials
            };
            return storageRequest;
        }
    }

    const getStatus = () => { return status ?? 0 }

    const getStatusLabel = () => {

        if (loading)
            return <CircularProgress size="2rem" />;

        switch (getStatus()) {
            case 1:
                return <Chip
                    label={"Draft Storage Request"}
                    color="default"
                    variant="filled"
                />;
            case 2:
                return <Chip
                    label={"Awaiting Approval - Open"}
                    color="primary"
                    variant="outlined"
                />;
            case 3: return <Chip
                label={"Approved - Open"}
                color="primary"
                variant="outlined"
            />;
            case 4: return <Chip
                label={"Rejected - Open"}
                color="primary"
                variant="outlined"
            />;
            case 5: return <Chip
                label={"Rejected - Closed"}
                color="default"
                variant="filled"
            />;
            case 6: return <Chip
                label={"Approved - Closed"}
                color="default"
                variant="filled"
            />;
            case 7: return <Chip
                label={"Cancelled - Closed"}
                color="default"
                variant="filled"
            />;
            default:
                return <Chip
                    label={"..."}
                    style={{ minWidth: '4rem' }}
                    color="primary"
                    variant="outlined"
                />;
        }

    }

    const getLinkLabel = () => {
        if (linkedC)
            return <Tooltip title="Go to contract / lease list" placement="right" >
                <Chip
                    sx={{ ml: 2, p: 0.5, cursor: 'pointer' }}
                    icon={<Topic />}
                    label={linkedC.reference}
                    color="primary"
                    variant="filled"
                    onClick={() => navigate('/contract/update/' + linkedC.contractId)}

                />
            </Tooltip>;

        return <></>;
    }

    const isReadonly = () => {
        return ![1, 4].includes(getStatus());
    }

    const isFormValid = () => {

        if (model) {

            if (!model.reasonForStorage || model.reasonForStorage.length === 0) return false;
            if (!model.standardPallets) return false;
            if (model.nonStandardPallets?.length === 0) return false;

            if (model.projectCodeRequired === true) {

                if (!model.collectionContactName || model.collectionContactName.length === 0) return false;
                if (!model.collectionContactNumber || model.collectionContactNumber.length === 0) return false;
                if (!model.collectionContactEmail || model.collectionContactEmail.length === 0) return false;
                if (!model.collectionAddressLine1 || model.collectionAddressLine1.length === 0) return false;
                if (!model.collectionAddressLine2 || model.collectionAddressLine2.length === 0) return false;
                if (!model.collectionCity || model.collectionCity.length === 0) return false;
                if (!model.collectionPostcode || model.collectionPostcode.length === 0) return false;
            }

            if (model.hazardousDeclarationAgreed !== true) return false;
            if (materials?.length === 0) return false;

            return true;
        } else return false;
    };


    return (
        <ExpandableContainer>
            <BreadcrumbBar
                current={bread}
                previous={[{
                    name: "Storage Request",
                    href: "/storagerequest"
                }]}
            />
            <Typography variant="h4" sx={{ py: 3 }}>{reference ? <>
                <Warehouse fontSize="inherit" sx={{ opacity: .8 }} color="primary" />
                {reference}
                <FormNavigation />
            </> : <CircularProgress />}</Typography>

            {getStatusLabel()}
            {getLinkLabel()}


            {model?.cancellationReason &&
                <Chip
                sx={{
                    my: 2, p: 0.5, display: "block", height: 'auto',
                    '& .MuiChip-label': {
                        display: 'block',
                        whiteSpace: 'normal',
                    }, }}
                    label={model.cancellationReason}
                    color="warning"
                    variant="outlined"
                />
            }

            <div className="form-section">
                <StorageRequestDetailForm
                    storageRequest={model}
                    documents={documents}
                    onUpdate={handleChange}
                    readonly={isReadonly()}
                    saving={unsavedChanges}
                />
            </div>

            <div className="form-section">
                <MaterialInformationForm
                    entityId={storageRequestId}
                    status={getStatus()}
                    materials={materials}
                    staticMaterials={staticMaterials}

                    onUpdateMaterials={onUpdateMaterials}
                    onUpdateMaterial={onUpdateMaterial}
                    onImportComplete={onImportComplete}
                    readonlyMaterialActions={discrepancyAlertSent}
                    readonly={isReadonly()}
                    saving={unsavedChanges}
                />
            </div>

            <div className="form-section">
                <DeliveryInformationForm
                    storageRequest={model}
                    onUpdate={handleChange}
                    readonly={isReadonly()}
                    saving={unsavedChanges}
                />
            </div>

            <div className="form-section">
                <HazardousDeclarationForm
                    storageRequest={model}
                    onUpdate={handleChange}
                    readonly={isReadonly()}
                    saving={unsavedChanges}
                />
            </div>

            <div className="form-section">
                {loading ? <LinearProgress /> :
                    <StorageRequestActions
                        formValid={isFormValid()}
                        materials={materials}
                        status={getStatus()}
                        {...{
                            onSubmit,
                            onDelete,
                            onApprove,
                            onAlert,
                            onReject,
                            onClose,
                            onComplete
                        }}
                        alertSent={discrepancyAlertSent}
                        readonly={false}
                    />
                }
            </div>
        </ExpandableContainer>
    );
}